import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Section } from "@/components/Section";
import { CtaPanel } from "@/components/CtaPanel";
import type { RangpurArea } from "@/content/rangpur-areas";
import { POSTS } from "@/content/posts";
import type { RangpurAreaView } from "./types";

/** Internal-link sections closing every Rangpur area page. */

export function AreaNearest({
  a,
  neighbors,
}: {
  a: RangpurArea;
  neighbors: RangpurAreaView["neighbors"];
}) {
  return (
    <Section
      eyebrow="Nearest neighborhoods"
      title={
        <>
          Closest to <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <p className="max-w-2xl text-muted-foreground -mt-6 mb-8">
        The nearest Rangpur areas where buyer journeys overlap with {a.name}. Most local SERPs blend
        these zones — covering all of them compounds your map-pack visibility.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {neighbors.map(({ area: n, km }) => (
          <Link
            key={n.slug}
            to="/seo-services/rangpur/$area"
            params={{ area: n.slug }}
            className="group rounded-3xl border border-border p-6 bg-card hover:border-primary transition"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              ≈ {km.toFixed(1)} km
            </div>
            <div className="mt-1 font-display text-lg leading-tight">SEO near {n.name}</div>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
              Explore <ArrowUpRight size={14} />
            </span>
          </Link>
        ))}
      </div>
    </Section>
  );
}

const PILL =
  "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 hover:border-primary";

export function AreaRelatedServices({
  a,
  relatedServices,
}: {
  a: RangpurArea;
  relatedServices: RangpurAreaView["relatedServices"];
}) {
  return (
    <Section
      eyebrow="Related services"
      title={
        <>
          What pairs with <span className="text-gradient">{a.name} SEO.</span>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-5">
        {relatedServices.map((r) => (
          <Link
            key={r.label + r.to + (r.hash ?? "")}
            to={r.to}
            hash={r.hash}
            className="group rounded-3xl border border-border p-7 bg-card hover:border-primary transition flex items-start gap-4"
          >
            <span className="mt-1 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition">
              <ArrowUpRight size={16} />
            </span>
            <div>
              <div className="font-display text-lg">{r.label}</div>
              <p className="mt-1 text-sm text-muted-foreground">{r.why}</p>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap gap-3 text-sm">
        <Link to="/seo-services/$location" params={{ location: "rangpur" }} className={PILL}>
          ← Back to Rangpur SEO hub
        </Link>
        <Link to="/case-studies" className={PILL}>
          Case studies
        </Link>
        <Link to="/process" className={PILL}>
          How I work
        </Link>
        <Link to="/about" className={PILL}>
          About Tamal
        </Link>
      </div>
    </Section>
  );
}

export function AreaAllAreas({ others }: { others: RangpurAreaView["others"] }) {
  return (
    <Section
      eyebrow="All Rangpur areas"
      title={
        <>
          Hyperlocal SEO across <span className="text-gradient">all of Rangpur.</span>
        </>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {others.map((o) => (
          <Link
            key={o.slug}
            to="/seo-services/rangpur/$area"
            params={{ area: o.slug }}
            className="group rounded-3xl border border-border p-6 bg-card hover:border-primary transition"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Rangpur
                </div>
                <div className="mt-1 font-display text-lg">SEO near {o.name}</div>
              </div>
              <ArrowUpRight size={18} className="text-muted-foreground group-hover:text-primary" />
            </div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

/** Related reads plus the closing call to action. */
export function AreaCta({ a }: { a: RangpurArea }) {
  return (
    <Section className="!pt-0">
      <AreaRelatedReads area={a} />
      <CtaPanel
        title={<>Own the "near {a.name}" map pack.</>}
        body={
          <>
            Most {a.name} competitors haven't claimed their Google Business Profile properly. Getting
            the basics right early can give you a head start, though no ranking is guaranteed.
          </>
        }
        primary={{ to: "/contact", label: <>Start a {a.name} SEO project</> }}
        secondary={{ to: "/seo-services/$location", params: { location: "rangpur" }, label: "See all Rangpur SEO" }}
      />
    </Section>
  );
}

/** Blog categories tried in order when picking related reads for an area page. */
const RELATED_READ_CATEGORIES = ["Local SEO", "AEO", "Technical SEO", "SEO Strategy"];

/**
 * Compact "Related reads" block surfacing 2 local-SEO blog posts on every
 * Rangpur area page, so hyperlocal landing pages link deeper into the blog.
 */
function AreaRelatedReads({ area }: { area: RangpurArea }) {
  // Prefer Local SEO posts, fall back to AEO + Technical SEO so we always
  // surface 2 picks even if a category has too few posts.
  const seen = new Set<string>();
  const picks = RELATED_READ_CATEGORIES.flatMap((cat) =>
    POSTS.filter((p) => p.category === cat).slice(0, 2),
  )
    .filter((p) => {
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    })
    .slice(0, 2);

  if (picks.length === 0) return null;

  return (
    <div className="mb-12 rounded-3xl border border-border bg-card/60 p-8 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary/80">Related reads</div>
          <h3 className="mt-2 font-display text-2xl md:text-3xl">
            Field notes that pair with <span className="text-gradient">{area.name}</span> SEO.
          </h3>
        </div>
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          All articles <ArrowUpRight size={14} />
        </Link>
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {picks.map((p) => (
          <Link
            key={p.slug}
            to="/blog/$slug"
            params={{ slug: p.slug }}
            className="group rounded-2xl border border-border bg-background p-5 hover:border-primary transition"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary/80">
              {p.category}
            </div>
            <div className="mt-2 font-display text-lg leading-snug group-hover:text-gradient">
              {p.title}
            </div>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
              Read article <ArrowUpRight size={12} />
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
